import React, {
  useState, useRef, useEffect, useMemo,
} from 'react';
import {
  Box,
  InputBase,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Chip,
  Divider,
  IconButton,
  Popper,
  ClickAwayListener,
  useTheme,
  CircularProgress,
  Avatar,
  Badge,
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Chat as ChatIcon,
  Description as FormIcon,
  AccountTree as FlowIcon,
  Settings as SettingsIcon,
  Dashboard as DashboardIcon,
  Analytics as AnalyticsIcon,
  Help as HelpIcon,
  Storage as DataIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  TrendingUp as TrendingIcon,
  AccessTime as RecentIcon,
  ViewModule as ViewModuleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axiosClient, { axiosClientV2 } from '../../api/axios.client';

/** Match server default cap in studioSearch.controller.js (max 200 per entity type). */
const STUDIO_SEARCH_DISPLAY_LIMIT = 100;

function GlobalSearch() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [typeaheadSuggestions, setTypeaheadSuggestions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [recentSearches, setRecentSearches] = useState([]);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const typeaheadTimeoutRef = useRef(null);

  // Static navigation and features data
  const staticData = [
    {
      category: 'Navigation',
      items: [
        {
          title: 'Home', description: 'Chat interface', path: '/dashboard', icon: ChatIcon, keywords: ['chat', 'home', 'conversation'],
        },
        {
          title: 'Dashboard', description: 'Overview and analytics', path: '/dashboard', icon: DashboardIcon, keywords: ['dashboard', 'overview', 'stats'],
        },
        {
          title: 'Form Builder', description: 'Create dynamic forms', path: '/formBuilder', icon: FormIcon, keywords: ['form', 'builder', 'create'],
        },
        {
          title: 'Agent Flow Builder', description: 'Design AI workflows', path: '/agentFlowBuilder', icon: FlowIcon, keywords: ['agent', 'flow', 'workflow', 'ai'],
        },
        {
          title: 'Analytics', description: 'Data insights', path: '/analytics', icon: AnalyticsIcon, keywords: ['analytics', 'data', 'insights', 'reports'],
        },
        {
          title: 'Data Sources', description: 'Manage data connections', path: '/data-sources', icon: DataIcon, keywords: ['data', 'sources', 'connections'],
        },
        {
          title: 'Settings', description: 'App configuration', path: '/settings', icon: SettingsIcon, keywords: ['settings', 'config', 'preferences'],
        },
        {
          title: 'Help & Support', description: 'Documentation and help', path: '/help', icon: HelpIcon, keywords: ['help', 'support', 'docs', 'documentation'],
        },
      ],
    },
    {
      category: 'Features',
      items: [
        {
          title: 'Text-to-Speech', description: 'Audio output settings', action: 'openSettings', icon: SettingsIcon, keywords: ['audio', 'speech', 'voice', 'tts'],
        },
        {
          title: 'AI Models', description: 'Change language model', action: 'openSettings', icon: SettingsIcon, keywords: ['model', 'ai', 'llama', 'deepseek'],
        },
        {
          title: 'Temperature', description: 'Adjust AI creativity', action: 'openSettings', icon: SettingsIcon, keywords: ['temperature', 'creativity', 'randomness'],
        },
        {
          title: 'File Upload', description: 'Upload images and PDFs', action: 'openSettings', icon: SettingsIcon, keywords: ['upload', 'file', 'image', 'pdf'],
        },
        {
          title: 'Quick Prompts', description: 'Pre-defined prompts', action: 'openSettings', icon: SettingsIcon, keywords: ['prompts', 'templates', 'quick'],
        },
      ],
    },
    {
      category: 'Actions',
      items: [
        {
          title: 'New Conversation', description: 'Start fresh chat', action: 'newConversation', icon: ChatIcon, keywords: ['new', 'conversation', 'chat', 'fresh'],
        },
        {
          title: 'Clear History', description: 'Clear current chat', action: 'clearHistory', icon: ChatIcon, keywords: ['clear', 'history', 'reset'],
        },
        {
          title: 'Export Chat', description: 'Download conversation', action: 'exportChat', icon: ChatIcon, keywords: ['export', 'download', 'save'],
        },
      ],
    },
  ];

  const staticDataWithStudio = useMemo(() => [
    {
      category: 'Studio',
      items: [
        {
          title: 'Studio home',
          description: 'Flows, apps, starters',
          path: '/studio',
          icon: DashboardIcon,
          keywords: ['studio', 'home', 'starters', 'templates', 'help'],
        },
        {
          title: 'V2 Flows',
          description: 'All saved flows',
          path: '/studio/flows',
          icon: FlowIcon,
          keywords: ['flows', 'v2', 'graph', 'workflow'],
        },
        {
          title: 'V2 Apps',
          description: 'All saved apps',
          path: '/studio/apps',
          icon: ViewModuleIcon,
          keywords: ['apps', 'layout', 'publish', 'preview'],
        },
        {
          title: 'Forms',
          description: 'Form designer and drafts',
          path: '/studio/forms',
          icon: FormIcon,
          keywords: ['forms', 'design', 'drafts', 'builder'],
        },
      ],
    },
    ...staticData,
  ], []);

  // Load recent searches from localStorage
  useEffect(() => {
    const recent = JSON.parse(localStorage.getItem('recentSearches') || '[]');
    setRecentSearches(recent.slice(0, 5)); // Keep only last 5
  }, []);

  // Save recent search
  const saveRecentSearch = (query) => {
    if (!query.trim() || query.length < 2) return;

    const recent = JSON.parse(localStorage.getItem('recentSearches') || '[]');
    const updated = [query, ...recent.filter((s) => s !== query)].slice(0, 5);
    localStorage.setItem('recentSearches', JSON.stringify(updated));
    setRecentSearches(updated);
  };

  // Typeahead suggestions API call
  const fetchTypeaheadSuggestions = async (query) => {
    if (!query.trim() || query.length < 2) {
      setTypeaheadSuggestions([]);
      return;
    }

    try {
      const response = await axiosClient.get(`search/typeahead?q=${encodeURIComponent(query)}`);
      let merged = [...(response.suggestions || [])];
      try {
        const data = await axiosClientV2.get(
          `search?q=${encodeURIComponent(query)}&limit=30`,
        );
        const names = [];
        (data?.flows || []).forEach((f) => {
          const n = f.name || f.definition?.name;
          if (n) names.push(n);
        });
        (data?.apps || []).forEach((a) => {
          const n = a.name || a.definition?.name;
          if (n) names.push(n);
        });
        (data?.forms || []).forEach((form) => {
          if (form.name) names.push(form.name);
        });
        merged = [...new Set([...merged, ...names])].slice(0, 24);
      } catch (_) {
        /* v2 unavailable or no tenant — keep v1 suggestions only */
      }
      setTypeaheadSuggestions(merged);
    } catch (error) {
      console.error('Typeahead error:', error);
      setTypeaheadSuggestions([]);
    }
  };

  // Full search backend function
  const searchBackend = async (query) => {
    try {
      setIsLoading(true);
      const results = await Promise.allSettled([
        // Search conversations
        axiosClient.get(`conversations/search?q=${encodeURIComponent(query)}`),
        // Search form designs
        axiosClient.get(`forms/designs/search?q=${encodeURIComponent(query)}`),
        // Search agentic flows
        axiosClient.get(`agentic-flows/search?q=${encodeURIComponent(query)}`),
        // Search users/collaborators
        axiosClient.get(`users/search?q=${encodeURIComponent(query)}`),
      ]);

      const backendResults = [];

      // Process conversations
      if (results[0].status === 'fulfilled' && results[0].value?.conversations) {
        const conversations = results[0].value.conversations.slice(0, 5);
        if (conversations.length > 0) {
          backendResults.push({
            category: 'Conversations',
            items: conversations.map((conv) => ({
              title: conv.name || 'Untitled Conversation',
              description: `${conv.messageCount || 0} messages • ${new Date(conv.updatedAt).toLocaleDateString()}`,
              path: `/conversation/${conv._id}`,
              icon: HistoryIcon,
              type: 'conversation',
              id: conv._id,
              avatar: conv.avatar,
            })),
          });
        }
      }

      // Process form designs
      if (results[1].status === 'fulfilled' && results[1].value?.forms) {
        const forms = results[1].value.forms.slice(0, 5);
        if (forms.length > 0) {
          backendResults.push({
            category: 'Forms',
            items: forms.map((form) => ({
              title: form.name,
              description: form.description || 'Form design',
              path: `/formBuilder?load=${form._id}`,
              icon: FormIcon,
              type: 'form',
              id: form._id,
              status: form.status,
            })),
          });
        }
      }

      // Process agentic flows
      if (results[2].status === 'fulfilled' && results[2].value?.flows) {
        const flows = results[2].value.flows.slice(0, 5);
        if (flows.length > 0) {
          backendResults.push({
            category: 'Workflows',
            items: flows.map((flow) => ({
              title: flow.name,
              description: flow.description || 'AI workflow',
              path: `/workflow/${flow._id}`,
              icon: FlowIcon,
              type: 'workflow',
              id: flow._id,
              status: flow.status,
            })),
          });
        }
      }

      // Process users
      if (results[3].status === 'fulfilled' && results[3].value?.users) {
        const users = results[3].value.users.slice(0, 3);
        if (users.length > 0) {
          backendResults.push({
            category: 'People',
            items: users.map((user) => ({
              title: user.username || user.email,
              description: `${user.role || 'User'} • Last active ${new Date(user.lastActive).toLocaleDateString()}`,
              path: `/profile/${user._id}`,
              icon: PersonIcon,
              type: 'user',
              id: user._id,
              avatar: user.avatar,
            })),
          });
        }
      }

      /** Studio V2: flows, apps, form designs — all brands / tenants (same scope as Studio list APIs). */
      try {
        const data = await axiosClientV2.get(
          `search?q=${encodeURIComponent(query)}&limit=${STUDIO_SEARCH_DISPLAY_LIMIT}`,
        );
        const flows = (data?.flows || []).slice(0, STUDIO_SEARCH_DISPLAY_LIMIT);
        const apps = (data?.apps || []).slice(0, STUDIO_SEARCH_DISPLAY_LIMIT);
        const studioForms = (data?.forms || []).slice(0, STUDIO_SEARCH_DISPLAY_LIMIT);
        const prepend = [];
        if (flows.length > 0) {
          prepend.push({
            category: 'Studio flows',
            items: flows.map((f) => ({
              title: f.name || f.definition?.name || 'Untitled flow',
              description: (f.definition?.description && String(f.definition.description).slice(0, 120))
                || `${f.status || 'draft'} · flow/v2`,
              path: `/studio/flows/${f._id}`,
              icon: FlowIcon,
              type: 'studio-flow',
              id: f._id,
            })),
          });
        }
        if (apps.length > 0) {
          prepend.push({
            category: 'Studio apps',
            items: apps.map((a) => ({
              title: a.name || a.definition?.name || 'Untitled app',
              description: (a.definition?.description && String(a.definition.description).slice(0, 120))
                || `${a.status || 'draft'} · app/v2`,
              path: `/studio/apps/${a._id}`,
              icon: ViewModuleIcon,
              type: 'studio-app',
              id: a._id,
            })),
          });
        }
        if (studioForms.length > 0) {
          prepend.push({
            category: 'Form designs',
            items: studioForms.map((form) => ({
              title: form.name || 'Untitled form',
              description: form.description || 'Open in form designer',
              path: `/formBuilder?load=${form._id}`,
              icon: FormIcon,
              type: 'form-design',
              id: form._id,
              status: form.status,
            })),
          });
        }
        return [...prepend, ...backendResults];
      } catch (e) {
        console.error('Studio search error:', e);
      }

      return backendResults;
    } catch (error) {
      console.error('Backend search error:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  // Combined search function
  const performSearch = async (query) => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    const lowerQuery = query.toLowerCase();

    // Search static data
    const staticResults = staticDataWithStudio.map((category) => ({
      category: category.category,
      items: category.items.filter((item) => item.title.toLowerCase().includes(lowerQuery)
        || item.description.toLowerCase().includes(lowerQuery)
        || item.keywords.some((keyword) => keyword.toLowerCase().includes(lowerQuery))),
    })).filter((category) => category.items.length > 0);

    // Search backend data
    const backendResults = await searchBackend(query);

    // Combine results - backend first, then static
    const combinedResults = [...backendResults, ...staticResults];
    setResults(combinedResults);

    // Save to recent searches
    saveRecentSearch(query);
  };

  // Debounced typeahead
  useEffect(() => {
    if (typeaheadTimeoutRef.current) {
      clearTimeout(typeaheadTimeoutRef.current);
    }

    typeaheadTimeoutRef.current = setTimeout(() => {
      fetchTypeaheadSuggestions(searchQuery);
    }, 150); // Faster for typeahead

    return () => {
      if (typeaheadTimeoutRef.current) {
        clearTimeout(typeaheadTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Debounced full search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      performSearch(searchQuery);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  const handleInputFocus = () => {
    setIsOpen(true);
    setSelectedIndex(-1);
    if (!searchQuery.trim()) {
      // Show recent searches and trending when focused with empty query
      showRecentAndTrending();
    }
  };

  const showRecentAndTrending = () => {
    const defaultResults = [];

    if (recentSearches.length > 0) {
      defaultResults.push({
        category: 'Recent Searches',
        items: recentSearches.map((search) => ({
          title: search,
          description: 'Recent search',
          action: 'search',
          icon: RecentIcon,
          query: search,
        })),
      });
    }

    // Add some trending/popular items
    defaultResults.push({
      category: 'Popular',
      items: [
        {
          title: 'Create new form', description: 'Most used action', action: 'openFormBuilder', icon: TrendingIcon,
        },
        {
          title: 'Chat with AI', description: 'Start conversation', action: 'newConversation', icon: TrendingIcon,
        },
        {
          title: 'View analytics', description: 'Check insights', path: '/analytics', icon: TrendingIcon,
        },
      ],
    });

    setResults(defaultResults);
  };

  const handleClickAway = () => {
    setIsOpen(false);
    setSelectedIndex(-1);
  };

  const handleResultClick = (item) => {
    if (item.query) {
      // It's a recent search
      setSearchQuery(item.query);
      performSearch(item.query);
      return;
    }

    if (item.path) {
      navigate(item.path);
    } else if (item.action) {
      handleAction(item.action);
    }

    setSearchQuery('');
    setIsOpen(false);
    setSelectedIndex(-1);
    inputRef.current?.blur();
  };

  const handleAction = (action) => {
    switch (action) {
      case 'openSettings':
        window.dispatchEvent(new Event('openSettings'));
        break;
      case 'newConversation':
        window.dispatchEvent(new Event('newConversation'));
        break;
      case 'clearHistory':
        window.dispatchEvent(new Event('clearHistory'));
        break;
      case 'exportChat':
        window.dispatchEvent(new Event('exportChat'));
        break;
      case 'openFormBuilder':
        navigate('/formBuilder');
        break;
      default:
        break;
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setResults([]);
    setTypeaheadSuggestions([]);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      setSelectedIndex(-1);
      inputRef.current?.blur();
      return;
    }

    const allItems = results.flatMap((category) => category.items);
    const totalItems = allItems.length;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % totalItems);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + totalItems) % totalItems);
    } else if (event.key === 'Enter' && selectedIndex >= 0) {
      event.preventDefault();
      handleResultClick(allItems[selectedIndex]);
    }
  };

  const handleInputChange = (e) => {
    setSearchQuery(e.target.value);
    setSelectedIndex(-1);
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion);
    performSearch(suggestion);
    inputRef.current?.focus();
  };

  // Get all flattened items for keyboard navigation
  const allItems = results.flatMap((category) => category.items);

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={{ position: 'relative', width: '100%', maxWidth: 400 }}>
        <Paper
          ref={searchRef}
          elevation={1}
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1,
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: theme.palette.primary.main,
            },
            '&:focus-within': {
              borderColor: theme.palette.primary.main,
              boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
            },
          }}
        >
          <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
          <InputBase
            ref={inputRef}
            placeholder="Search conversations, forms, features..."
            value={searchQuery}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            sx={{
              flex: 1,
              fontSize: '0.875rem',
              '& .MuiInputBase-input': {
                padding: 0,
              },
            }}
          />
          {isLoading && (
            <CircularProgress size={16} sx={{ mr: 1 }} />
          )}
          {searchQuery && !isLoading && (
            <IconButton
              size="small"
              onClick={handleClearSearch}
              sx={{ ml: 1, p: 0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Paper>

        {/* Typeahead Suggestions */}
        {typeaheadSuggestions.length > 0 && searchQuery.trim() && (
          <Paper
            elevation={4}
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              mt: 0.5,
              zIndex: 1301,
              bgcolor: 'background.paper',
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <List dense>
              {typeaheadSuggestions.map((suggestion, index) => (
                <ListItem
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                  sx={{
                    cursor: 'pointer',
                    py: 0.5,
                    px: 2,
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {suggestion}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Paper>
        )}

        {/* Main Search Results Dropdown */}
        <Popper
          open={isOpen && (searchQuery.trim() || results.length > 0)}
          anchorEl={searchRef.current}
          placement="bottom-start"
          style={{ zIndex: 1300, width: searchRef.current?.offsetWidth }}
        >
          <Paper
            elevation={8}
            sx={{
              mt: typeaheadSuggestions.length > 0 ? 6 : 1,
              maxHeight: 500,
              overflow: 'auto',
              bgcolor: 'background.paper',
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            {searchQuery.trim() && results.length === 0 && !isLoading ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No results found for "
                  {searchQuery}
                  "
                </Typography>
              </Box>
            ) : (
              <List dense sx={{ py: 1 }}>
                {results.map((category, categoryIndex) => (
                  <React.Fragment key={category.category}>
                    {categoryIndex > 0 && <Divider />}
                    <ListItem>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color: 'text.secondary',
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                        }}
                      >
                        {category.category}
                      </Typography>
                    </ListItem>
                    {category.items.map((item, itemIndex) => {
                      const IconComponent = item.icon;
                      const flatIndex = results.slice(0, categoryIndex)
                        .reduce((acc, cat) => acc + cat.items.length, 0) + itemIndex;
                      const isSelected = selectedIndex === flatIndex;

                      return (
                        <ListItem
                          key={`${category.category}-${itemIndex}`}
                          onClick={() => handleResultClick(item)}
                          sx={{
                            cursor: 'pointer',
                            pl: 3,
                            bgcolor: isSelected ? 'action.selected' : 'transparent',
                            '&:hover': {
                              bgcolor: 'action.hover',
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {item.avatar ? (
                              <Avatar
                                src={item.avatar}
                                sx={{ width: 24, height: 24 }}
                              >
                                {item.title.charAt(0).toUpperCase()}
                              </Avatar>
                            ) : (
                              <IconComponent
                                fontSize="small"
                                sx={{ color: 'text.secondary' }}
                              />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={(
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {item.title}
                                </Typography>
                                {item.action && (
                                  <Chip
                                    label="Action"
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: '0.6rem' }}
                                  />
                                )}
                                {item.type && (
                                  <Chip
                                    label={item.type}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: '0.6rem' }}
                                  />
                                )}
                                {item.status && (
                                  <Badge
                                    badgeContent={item.status}
                                    color={item.status === 'published' ? 'success' : 'default'}
                                    sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 16 } }}
                                  />
                                )}
                              </Box>
                            )}
                            secondary={(
                              <Typography variant="caption" color="text.secondary">
                                {item.description}
                              </Typography>
                            )}
                          />
                        </ListItem>
                      );
                    })}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}

export default GlobalSearch;
